// ╔══════════════════════════════════════════════════════════════╗
// ║  Login page — animated background, phone-only, no hints       ║
// ╚══════════════════════════════════════════════════════════════╝

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { getErrorMessage } from '../lib/api.js';
import Icon from '../components/Icon.jsx';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(
    () => localStorage.getItem('hindam_theme') || 'dark',
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('hindam_theme', theme);
  }, [theme]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(identifier, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  // Phone-only login. Valid Egyptian phone: 11 digits starting with 01.
  const looksLikePhone = /^01\d{9}$/.test(identifier.replace(/\s|-/g, ''));

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.1fr_1fr] bg-ink-50 dark:bg-ink-950 relative overflow-hidden">
      {/* ── Animated background (left, desktop only) ── */}
      <div className="hidden lg:block relative bg-gradient-to-br from-brand-950 via-ink-900 to-brand-900 overflow-hidden">
        {/* Concentric arcs */}
        <svg
          className="absolute inset-0 w-full h-full opacity-50"
          viewBox="0 0 800 800"
          style={{ animation: 'slowSpin 60s linear infinite' }}
        >
          <defs>
            <radialGradient id="rg" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#cc9c46" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#cc9c46" stopOpacity="0" />
            </radialGradient>
          </defs>
          {[120, 200, 290, 380, 470].map((r, i) => (
            <circle
              key={i}
              cx="400"
              cy="400"
              r={r}
              fill="none"
              stroke="#cc9c46"
              strokeOpacity={0.12 - i * 0.018}
              strokeWidth="1"
            />
          ))}
          <circle cx="400" cy="400" r="380" fill="url(#rg)" />
        </svg>

        {/* Grain dots */}
        <div className="absolute inset-0 dot-grid opacity-50" />

        {/* Top branding strip */}
        <div className="absolute top-10 inset-x-10 flex items-center justify-between text-brand-100/80">
          <div className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt=""
              className="w-10 h-10 rounded-lg ring-1 ring-brand-300/30 object-cover"
            />
            <div className="text-xs font-semibold tracking-widest uppercase">
              رداء هندام
            </div>
          </div>
          <div className="text-[11px] font-mono opacity-70">v2 · hindam-erp</div>
        </div>

        {/* Floating logo */}
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="inline-block" style={{ animation: 'float 6s ease-in-out infinite' }}>
            <img
              src="/logo.jpg"
              alt="رداء هندام"
              className="w-56 h-56 rounded-[2rem] ring-2 ring-brand-300/40 shadow-2xl mx-auto object-cover"
            />
          </div>
        </div>

        {/* Bottom strip */}
        <div className="absolute bottom-6 inset-x-10 flex items-center justify-between text-[11px] text-brand-100/50">
          <span>© 2026 رداء هندام</span>
          <span className="font-mono">ISO/IEC 27001 · مرخّص</span>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-12px); }
          }
          @keyframes slowSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* ── Form (right) ── */}
      <div className="flex flex-col p-6 md:p-10 lg:p-14 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/logo.jpg"
              alt=""
              className="w-9 h-9 rounded-lg ring-1 ring-brand-700/30 object-cover lg:hidden"
            />
            <div className="leading-tight lg:hidden">
              <div className="font-extrabold text-[14px]">رداء <span className="text-brand-500">هندام</span></div>
            </div>
          </div>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="h-9 w-9 grid place-items-center rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800"
            aria-label="تبديل النمط"
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
          </button>
        </div>

        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-md">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              رداء هندام · نظام متكامل
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tight">
              مرحباً بعودتك
            </h2>
            <p className="mt-2 text-sm text-ink-400">
              سجّل دخولك للوصول إلى لوحة التحكم.
            </p>

            {error && (
              <div className="mt-5 px-3 py-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 text-sm flex items-start gap-2">
                <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold mb-1.5 text-ink-500">
                  رقم التليفون
                </span>
                <div className="relative">
                  <Icon
                    name="fingerprint"
                    className="absolute top-3.5 right-3 text-ink-400"
                    size={16}
                  />
                  <input
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    dir="ltr"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="username"
                    required
                    placeholder="01xxxxxxxxx"
                    className="w-full h-12 px-4 pe-10 rounded-xl bg-ink-50 dark:bg-ink-900 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none font-medium text-end"
                  />
                </div>
                {identifier && !looksLikePhone && (
                  <span className="text-[11px] text-amber-600 mt-1 block">
                    أدخل رقم تليفون صحيح مكوّن من 11 رقم (يبدأ بـ 01)
                  </span>
                )}
              </label>

              <label className="block">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-ink-500">
                    كلمة المرور
                  </span>
                  <span className="text-[11px] text-brand-500 font-semibold opacity-50">
                    تواصل مع المسؤول
                  </span>
                </div>
                <div className="relative">
                  <Icon
                    name="shield"
                    className="absolute top-3.5 right-3 text-ink-400"
                    size={16}
                  />
                  <input
                    value={password}
                    type={showPwd ? 'text' : 'password'}
                    onChange={(e) => setPassword(e.target.value)}
                    dir="ltr"
                    autoComplete="current-password"
                    required
                    className="w-full h-12 px-4 pe-10 ps-10 rounded-xl bg-ink-50 dark:bg-ink-900 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none font-medium text-end"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute top-1/2 -translate-y-1/2 left-3 text-ink-400 hover:text-ink-900 dark:hover:text-ink-50"
                    aria-label="إظهار/إخفاء كلمة المرور"
                  >
                    <Icon name={showPwd ? 'moon' : 'sun'} size={16} />
                  </button>
                </div>
              </label>

              <div className="flex items-center justify-between text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="toggle"
                  />
                  <span className="text-ink-500">تذكّرني</span>
                </label>
                <span className="text-[11px] text-ink-400 font-mono">
                  جلسة آمنة · TLS 1.3
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-bold bg-gradient-to-b from-brand-400 to-brand-600 text-ink-950 hover:from-brand-300 hover:to-brand-500 shadow-pop transition disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-ink-950/40 border-t-ink-950 rounded-full animate-spin" />
                    جارٍ التحقّق…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    دخول إلى لوحة التحكّم
                    <Icon name="arrowL" size={16} />
                  </span>
                )}
              </button>
            </form>

            <div className="mt-8 text-[11px] text-ink-400 flex items-center gap-2 justify-center">
              <Icon name="shield" size={12} className="text-emerald-500" />
              اتصال مشفّر · AES-256 · بيانات محمية
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
